import type { Subscription, SubscriptionBillingCycle } from '@bugbug/core/types/subscriptions';
import type { Maybe } from '@bugbug/core/types/utils';
import {
  SUBSCRIPTION_BILLING_CYCLE_TIERS,
  SUBSCRIPTION_TYPE_TIERS,
} from '~/constants/subscription';
import i18n from '~/translations';

export const comparePlans = (a: Maybe<Subscription>, b: Maybe<Subscription>) => {
  const typeCompare =
    SUBSCRIPTION_TYPE_TIERS.indexOf(a?.type ?? 'free') -
    SUBSCRIPTION_TYPE_TIERS.indexOf(b?.type ?? 'free');

  if (typeCompare !== 0) {
    return typeCompare;
  }

  return (
    SUBSCRIPTION_BILLING_CYCLE_TIERS.indexOf(a?.billingCycle ?? 'monthly') -
    SUBSCRIPTION_BILLING_CYCLE_TIERS.indexOf(b?.billingCycle ?? 'monthly')
  );
};

export const isLowerPlan = (a: Maybe<Subscription>, b: Maybe<Subscription>) =>
  comparePlans(a, b) < 0;

export const isHigherPlan = (a: Maybe<Subscription>, b: Maybe<Subscription>) =>
  comparePlans(a, b) > 0;

export const isSamePlan = (a: Maybe<Subscription>, b: Maybe<Subscription>) =>
  comparePlans(a, b) === 0;

export const getParallelsLabel = (number: number) =>
  i18n.t('parallels.label', {
    defaultValue_one: '1 test run at a time',
    defaultValue_other: '{{count}} parallel runs',
    count: number + 1,
  });

export const getBillingLabel = (billingCycle: SubscriptionBillingCycle) => {
  if (billingCycle === 'yearly') {
    return i18n.t('billing.label.yearly', 'Billed annually');
  }

  return i18n.t('billing.label.monthly', 'Billed monthly');
};

export const getSubscriptionPlanLabel = ({
  plan,
  parallels,
  includeBillingCycle = false,
}: {
  plan: Subscription;
  parallels: number;
  includeBillingCycle?: boolean;
}) => {
  const params = {
    name: plan.type.toUpperCase(),
    parallelLabel: getParallelsLabel(parallels),
    billingCycle: getBillingLabel(plan.billingCycle).toLowerCase(),
  };

  if (plan.type === 'free') {
    return i18n.t('subscriptionPlan.label.free', 'FREE');
  }

  if (includeBillingCycle) {
    return i18n.t(
      'subscriptionPlan.labelWithBillingCycle',
      '{{name}} with {{parallelLabel}} ({{billingCycle}})',
      params,
    );
  }

  return i18n.t('subscriptionPlan.label.default', '{{name}} with {{parallelLabel}}', params);
};
